import { Tag, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type {
  SavedSearchParameters,
  SearchFilterField,
  SearchFilterValue,
} from 'lib-shared-types-api';
import isEmpty from 'lodash/isEmpty';
import { Fragment, useMemo } from 'react';

import { convertSortObjectToString } from '../../../utils/sort/convertSort';
import {
  filterFieldAssets,
  filterOperatorLabels,
} from '../../filters/utils/definitions';
import { sortOptions } from '../../results/components/sort/searchResultsSort';

import styles from './savedSearchFilters.module.scss';

type SavedSearchFiltersProps = {
  variant: 'text' | 'tags';
  params: SavedSearchParameters;
  onRemove?: (field: 'query' | SearchFilterField | SearchFilterField[]) => void;
};

type FilterRenderItem = {
  value: string;
  label: string;
  operator?: string;
  onDelete?: VoidFunction;
};

/**
 * Represents a component that is used to render the saved search filters that
 * are applied
 */
export const SavedSearchFilters = ({
  variant,
  onRemove,
  params,
}: SavedSearchFiltersProps): JSX.Element => {
  const buildFilterSummary = (values: SearchFilterValue[]): string => {
    return values.map((item) => item.label || item.value).join(', ');
  };

  const filtersToRender = useMemo(() => {
    const { query, filters, strict, sort } = params;
    const items: FilterRenderItem[] = [];

    // The search query is always first, if it exists
    if (query) {
      items.push(
        {
          label: 'Keyword',
          value: query,
          onDelete: isEmpty(filters?.conditions)
            ? undefined
            : () => onRemove?.('query'),
        },
        {
          label: 'Exact match',
          value: strict ? 'Enabled' : 'Disabled',
        },
      );
    }

    filters?.conditions.forEach(({ field, operator, values }) => {
      items.push({
        label: filterFieldAssets[field]?.label ?? field,
        operator: filterOperatorLabels[operator],
        value: buildFilterSummary(values),
        onDelete: () => onRemove?.(field),
      });
    });

    items.push({
      label: 'Sort by',
      value:
        (sort &&
          sortOptions.find(
            ({ value }) => value === convertSortObjectToString(sort),
          )?.label) ||
        'Relevance',
    });

    return items;
  }, [onRemove, params]);

  const rootClass = classnames(styles.filters, {
    [styles.filtersTags]: variant === 'tags',
  });

  return (
    <div className={rootClass}>
      {filtersToRender.map(({ value, label = '', operator, onDelete }, i) => {
        const isLast = i === filtersToRender.length - 1;
        const separator = !isLast ? (
          <span className={styles.filtersTextSeparator}>•</span>
        ) : (
          ''
        );

        if (variant === 'tags') {
          return (
            <Tag
              className={styles.filtersTag}
              key={label}
              onDelete={onDelete}
              noTruncation
            >
              <span className={styles.filtersTagLabel}>{label}</span>
              {operator && (
                <span className={styles.filtersTagOperator}>{operator}</span>
              )}
              <span>{value}</span>
            </Tag>
          );
        }

        return (
          <Fragment key={label}>
            <Text.Paragraph variant="4" color="secondary" as="span">
              {label}: {operator} {value}
            </Text.Paragraph>
            {separator}
          </Fragment>
        );
      })}
    </div>
  );
};
